import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT_CLASS = "bg-navy-dark border-gold/20 text-white placeholder:text-gray-600 focus-visible:ring-gold/40 h-12";

const EMPTY_FORM = { full_name: "", email: "", phone: "", practice_area: "", message: "" };

const ContactInfo = () => (
  <div className="animate-on-scroll space-y-8">
    <div>
      <p className="text-sm tracking-[0.3em] uppercase text-gold font-body font-medium mb-4">
        Book a Consultation
      </p>
      <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-white leading-tight">
        Tell us about your matter.
      </h2>
      <p className="text-base text-gray-400 font-body mt-4 leading-relaxed">
        Every inquiry is confidential. Share your priorities and we will assemble the right legal strategy for your goals.
      </p>
      <div className="h-px w-16 bg-gold/40 mt-6" />
    </div>

    <div className="space-y-6">
      <a
        href="mailto:garvilsingh@lexsant.com"
        data-testid="contact-email"
        className="flex items-center gap-4 text-gray-300 hover:text-gold transition-colors group"
      >
        <div className="w-12 h-12 border border-gold/20 flex items-center justify-center group-hover:border-gold/60 transition-colors">
          <Mail className="w-5 h-5 text-gold" />
        </div>
        <div>
          <p className="text-xs tracking-widest uppercase text-gold/50 font-body">Email</p>
          <p className="text-sm font-body">garvilsingh@lexsant.com</p>
          <p className="text-sm font-body">samarthdhumal@lexsant.com</p>
        </div>
      </a>

      <a
        href="tel:+919914601473"
        data-testid="contact-phone"
        className="flex items-center gap-4 text-gray-300 hover:text-gold transition-colors group"
      >
        <div className="w-12 h-12 border border-gold/20 flex items-center justify-center group-hover:border-gold/60 transition-colors">
          <Phone className="w-5 h-5 text-gold" />
        </div>
        <div>
          <p className="text-xs tracking-widest uppercase text-gold/50 font-body">Phone</p>
          <p className="text-sm font-body">+91-9914601473</p>
        </div>
      </a>

      <div className="flex items-center gap-4 text-gray-300" data-testid="contact-address">
        <div className="w-12 h-12 border border-gold/20 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-gold" />
        </div>
        <div>
          <p className="text-xs tracking-widest uppercase text-gold/50 font-body">Office</p>
          <p className="text-sm font-body">C298, Defence Colony, New Delhi, India</p>
        </div>
      </div>
    </div>

    <p className="text-xs text-gold/40 font-body italic">
      All enquiries are strictly confidential.
    </p>
  </div>
);

const PracticeAreaSelect = ({ value, onChange }) => (
  <div className="space-y-2">
    <Label className="text-sm text-gray-300 font-body">Practice Area *</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid="form-practice-area"
        className="bg-navy-dark border-gold/20 text-white h-12 focus:ring-gold/40"
      >
        <SelectValue placeholder="Select a practice area" />
      </SelectTrigger>
      <SelectContent className="bg-navy-base border-gold/20">
        <SelectItem value="Sports Law" data-testid="select-sports-law">Sports Law</SelectItem>
        <SelectItem value="Taxation" data-testid="select-taxation">Taxation Advisory & Litigation</SelectItem>
        <SelectItem value="Criminal Litigation" data-testid="select-criminal">Criminal Litigation</SelectItem>
        <SelectItem value="Civil Litigation" data-testid="select-civil">Civil Litigation</SelectItem>
        <SelectItem value="Other" data-testid="select-other">Other</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const ConsultationForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.practice_area) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultation`, form);
      toast.success("Thank you. We will respond within 24 hours.");
      setForm(EMPTY_FORM);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="consultation-form">
      <div className="space-y-2">
        <Label className="text-sm text-gray-300 font-body">Full Name *</Label>
        <Input
          name="full_name"
          value={form.full_name}
          onChange={handleChange}
          placeholder="Your full name"
          data-testid="form-full-name"
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-sm text-gray-300 font-body">Email Address *</Label>
          <Input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="your@email.com"
            data-testid="form-email"
            className={INPUT_CLASS}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-gray-300 font-body">Phone Number *</Label>
          <Input
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="+91-XXXXXXXXXX"
            data-testid="form-phone"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <PracticeAreaSelect
        value={form.practice_area}
        onChange={(val) => setForm({ ...form, practice_area: val })}
      />

      <div className="space-y-2">
        <Label className="text-sm text-gray-300 font-body">Brief Description (Optional)</Label>
        <Textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Briefly describe your matter..."
          data-testid="form-message"
          rows={3}
          className="bg-navy-dark border-gold/20 text-white placeholder:text-gray-600 focus-visible:ring-gold/40 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="form-submit-btn"
        className="w-full bg-gold text-navy-dark py-4 text-sm font-semibold tracking-wide hover:bg-gold-hover transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? "Submitting..." : "Request Consultation"}
        {!loading && <Send className="w-4 h-4" />}
      </button>

      <p className="text-xs text-center text-gold/40 font-body italic">
        All enquiries are strictly confidential.
      </p>
    </form>
  );
};

export default function ContactForm() {
  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-navy-base">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <ContactInfo />
          <div className="animate-on-scroll">
            <ConsultationForm />
          </div>
        </div>
      </div>
    </section>
  );
}
